import React, { useState, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet';
import { Search, Layers, TrendingUp, ChevronRight } from 'lucide-react';
import { useProviders, useGameEngine, getProviderImage } from 'hooks/useGameEngine';

// ─── Animations ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

// ─── Layout ────────────────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  flex: 1;
  min-width: 0;
  padding: 0 0 48px 0;
  animation: ${fadeUp} 0.35s ease both;
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #1a1f2e 0%, #0d1117 60%, #111827 100%);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  padding: 40px 32px 32px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -60px; right: -60px;
    width: 300px; height: 300px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(37,222,130,0.07) 0%, transparent 70%);
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 900;
  color: #ffffff;
  margin: 0 0 6px;
  letter-spacing: -0.04em;
`;

const HeroSub = styled.p`
  color: #94a6b5;
  font-size: 15px;
  margin: 0;
`;

const ContentArea = styled.div`
  padding: 28px 32px 0;

  @media (max-width: 768px) {
    padding: 20px 16px 0;
  }
`;

// ─── Search ────────────────────────────────────────────────────────────────────
const SearchWrapper = styled.div`
  position: relative;
  max-width: 420px;
  margin-bottom: 28px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a6b5;
  display: flex;
  align-items: center;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  background: #1a1f2e;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 12px 16px 12px 42px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  outline: none;
  transition: border-color 0.2s;

  &:focus { border-color: rgba(37,222,130,0.4); }
  &::placeholder { color: #4a5568; }
`;

// ─── Stats Bar ─────────────────────────────────────────────────────────────────
const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a6b5;
  font-size: 13px;
  font-weight: 600;

  svg { color: #25de82; }
  span { color: #fff; font-size: 15px; font-weight: 800; }
`;

// ─── Grid ──────────────────────────────────────────────────────────────────────
const ProvidersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (min-width: 480px)  { grid-template-columns: repeat(3, 1fr); }
  @media (min-width: 768px)  { grid-template-columns: repeat(4, 1fr); }
  @media (min-width: 1024px) { grid-template-columns: repeat(5, 1fr); }
  @media (min-width: 1280px) { grid-template-columns: repeat(7, 1fr); }
`;

// ─── Provider Card ─────────────────────────────────────────────────────────────
const ProviderCard = styled.div`
  background: #1a1f2e;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  padding: 20px 12px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
  text-decoration: none;
  aspect-ratio: 4 / 3;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(37,222,130,0.05) 0%, transparent 60%);
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    border-color: rgba(37,222,130,0.25);
    transform: translateY(-3px);
    background: #1e2538;
    &::after { opacity: 1; }
  }

  &:active { transform: scale(0.97); }
`;

const ProviderLogo = styled.img`
  width: 100%;
  max-width: 90px;
  height: 44px;
  object-fit: contain;
  filter: brightness(0.9);
  transition: filter 0.2s;

  ${ProviderCard}:hover & { filter: brightness(1.1); }
`;

const ProviderLogoFallback = styled.div`
  width: 90px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(255,255,255,0.04);
`;

const ProviderName = styled.span`
  font-size: 11px;
  font-weight: 800;
  color: #94a6b5;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1.3;
  transition: color 0.2s;

  ${ProviderCard}:hover & { color: #ffffff; }
`;

const PlayingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  color: #94a6b5;
`;

const GreenDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #25de82;
  box-shadow: 0 0 6px rgba(37,222,130,0.7);
  flex-shrink: 0;
`;

// ─── Skeleton Loader ───────────────────────────────────────────────────────────
const SkeletonCard = styled.div`
  background: linear-gradient(90deg, #1a1f2e 25%, #1e2538 50%, #1a1f2e 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite;
  border-radius: 12px;
  aspect-ratio: 4 / 3;
  border: 1px solid rgba(255,255,255,0.04);
`;

// ─── Load More ─────────────────────────────────────────────────────────────────
const LoadMoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 36px;
  margin-bottom: 8px;
`;

const Line = styled.div`
  flex: 1;
  height: 1px;
  background: rgba(255,255,255,0.05);
`;

const LoadMoreBtn = styled.button`
  background: none;
  border: none;
  color: #94a6b5;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  cursor: pointer;
  transition: color 0.2s;
  white-space: nowrap;

  &:hover { color: #ffffff; }
`;

// ─── Empty State ───────────────────────────────────────────────────────────────
const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #94a6b5;
`;

// ─── Main Component ────────────────────────────────────────────────────────────
const INITIAL_COUNT = 28;
const LOAD_STEP = 28;

const ProvidersPage = () => {
  const history = useHistory();
  const { data: providers = [], isLoading } = useProviders();
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);
  const [imgErrors, setImgErrors] = useState({});

  const filtered = useMemo(() => {
    if (!search.trim()) return providers;
    const q = search.toLowerCase();
    return providers.filter(p => p.name?.toLowerCase().includes(q));
  }, [providers, search]);

  const displayed = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  const getMockPlaying = (provider) => {
    const seed = (provider.name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    return 50 + (seed % 8000);
  };

  const handleImgError = (id) => {
    setImgErrors(prev => ({ ...prev, [id]: true }));
  };

  const goToProvider = (provider) => {
    history.push(`/casino/provider/${provider.id}`);
  };

  return (
    <PageWrapper>
      <Helmet>
        <title>Game Providers — Casino</title>
        <meta name="description" content="Browse all game providers and publishers available in our casino." />
      </Helmet>

      {/* Hero */}
      <HeroSection>
        <HeroTitle>Game Providers</HeroTitle>
        <HeroSub>Browse all publishers and studios powering our casino</HeroSub>
      </HeroSection>

      <ContentArea>
        {/* Stats */}
        <StatsBar>
          <StatItem>
            <Layers size={16} />
            <span>{providers.length}</span> Total Providers
          </StatItem>
          <StatItem>
            <TrendingUp size={16} />
            Active &amp; Verified Publishers
          </StatItem>
        </StatsBar>

        {/* Search */}
        <SearchWrapper>
          <SearchIcon><Search size={16} /></SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search providers..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisibleCount(INITIAL_COUNT); }}
          />
        </SearchWrapper>

        {/* Grid */}
        {isLoading ? (
          <ProvidersGrid>
            {Array.from({ length: 21 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </ProvidersGrid>
        ) : displayed.length === 0 ? (
          <EmptyState>
            <Layers size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
            <p style={{ fontSize: 16, fontWeight: 700 }}>No providers found</p>
            <p style={{ fontSize: 13, marginTop: 4 }}>Try a different search term</p>
          </EmptyState>
        ) : (
          <>
            <ProvidersGrid>
              {displayed.map((provider) => {
                const logoUrl = getProviderImage(provider);
                const hasImgError = imgErrors[provider.id];
                const playing = getMockPlaying(provider);

                return (
                  <ProviderCard key={provider.id} onClick={() => goToProvider(provider)}>
                    {logoUrl && !hasImgError ? (
                      <ProviderLogo
                        src={logoUrl}
                        alt={provider.name}
                        onError={() => handleImgError(provider.id)}
                      />
                    ) : (
                      <ProviderLogoFallback>
                        <Layers size={28} color="#4a5568" />
                      </ProviderLogoFallback>
                    )}
                    <ProviderName>{provider.name}</ProviderName>
                    <PlayingBadge>
                      <GreenDot />
                      {playing.toLocaleString()} playing
                    </PlayingBadge>
                  </ProviderCard>
                );
              })}
            </ProvidersGrid>

            {hasMore && (
              <LoadMoreRow>
                <Line />
                <LoadMoreBtn onClick={() => setVisibleCount(v => v + LOAD_STEP)}>
                  Load More
                </LoadMoreBtn>
                <Line />
              </LoadMoreRow>
            )}
          </>
        )}
      </ContentArea>
    </PageWrapper>
  );
};

export default ProvidersPage;
